import * as XLSX from 'xlsx';
import { EM_DASH } from './formatters';

/**
 * Excel + PDF export helpers for the native report pages.
 *
 * Excel: build array-of-array sheets from the same column/row configs the
 * matrix and detail tables already use, so every page exports exactly what
 * it renders (current filters included).
 *
 * PDF: `printPage()` triggers the browser print dialog — combined with the
 * @media print rules in ReportsNative.css this yields an exact rendering of
 * the page as currently filtered (user picks "Save as PDF").
 */

function cellValue(value) {
  if (value === null || value === undefined || value === EM_DASH) return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  return value;
}

/** Matrix config → array-of-arrays (indented hierarchy labels, footer last). */
export function matrixToAoa({ columns = [], rows = [], footer = null, rowHeaderLabel = '' }) {
  const header = [rowHeaderLabel, ...columns.map((c) => (c.group ? `${c.group} ${c.label}` : c.label))];
  const levelOf = (row, byId) => {
    let level = 0;
    let cur = row;
    while (cur && cur.parentId && byId.has(cur.parentId)) {
      level += 1;
      cur = byId.get(cur.parentId);
    }
    return row.level ?? level;
  };
  const byId = new Map(rows.map((r) => [r.id, r]));
  const body = rows.map((r) => [
    `${'    '.repeat(levelOf(r, byId))}${r.label ?? ''}`,
    ...columns.map((c) => cellValue(r.cells?.[c.key])),
  ]);
  const foot = footer ? [[footer.label ?? 'Total', ...columns.map((c) => cellValue(footer.cells?.[c.key]))]] : [];
  return [header, ...body, ...foot];
}

/** Flat table config → array-of-arrays. */
export function tableToAoa(columns = [], rows = [], footer = null) {
  const header = columns.map((c) => c.label);
  const body = rows.map((r) => columns.map((c) => cellValue(r[c.key])));
  const foot = footer
    ? [columns.map((c, i) => (i === 0 ? footer.label ?? 'Total' : cellValue(footer.cells?.[c.key])))]
    : [];
  return [header, ...body, ...foot];
}

/**
 * Download a workbook. sheets = [{ name, aoa }] (aoa = array of arrays).
 * Column widths are sized from content; numbers stay numeric in Excel.
 */
export function downloadXlsx(filename, sheets) {
  const wb = XLSX.utils.book_new();
  (sheets || []).forEach((sheet, idx) => {
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa || []);
    const widths = [];
    (sheet.aoa || []).forEach((row) =>
      row.forEach((cell, c) => {
        const len = cell === null || cell === undefined ? 0 : String(cell).length;
        widths[c] = Math.min(48, Math.max(widths[c] || 10, len + 2));
      })
    );
    ws['!cols'] = widths.map((wch) => ({ wch }));
    const name = (sheet.name || `Sheet${idx + 1}`).replace(/[\\/?*[\]:]/g, ' ').substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

/** Exact-render PDF via the browser print dialog (see @media print CSS). */
export function printPage() {
  window.print();
}
